/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState } from 'react';
import { Line, Doughnut, Bar, Radar } from 'react-chartjs-2';
import { useTheme } from '../contexts/ThemeContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Props {
  metrics: any;
  brandName: string;
}

/**
 * Enhanced Digital Brand Health Dashboard
 * Shows comprehensive social media, search trends, content, and digital engagement metrics
 */
const DigitalMetricsDashboard: React.FC<Props> = ({ metrics, brandName }) => {
  const { isDark } = useTheme();
  const [activeView, setActiveView] = useState<'social' | 'content' | 'search' | 'paid'>('social');
  const [contentTypeFilter, setContentTypeFilter] = useState<'all' | 'brand' | 'community'>('all');
  
  // Handle both old and new metric structures
  const isEnhancedMetrics = metrics?.social_media?.platforms;
  
  // Sentiment distribution for both old and new structures
  const positiveValue = isEnhancedMetrics
    ? (metrics.social_media?.platforms?.Twitter?.sentiment_score || 70)
    : (metrics.positive_mentions || 65);
  const negativeValue = isEnhancedMetrics ? 10 : (metrics.negative_mentions || 10);
  const neutralValue = isEnhancedMetrics
    ? (100 - positiveValue - negativeValue)
    : (metrics.neutral_mentions || 25);

  const sentimentData = {
    labels: [`Positive`, `Neutral`, `Negative`],
    datasets: [{
      data: [positiveValue, neutralValue, negativeValue],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',
        'rgba(156, 163, 175, 0.8)',
        'rgba(239, 68, 68, 0.8)'
      ],
      borderWidth: 0
    }]
  };

  // Platform performance for enhanced metrics
  const platformData = isEnhancedMetrics ? {
    labels: Object.keys(metrics.social_media?.platforms || {}),
    datasets: [{
      label: 'Followers',
      data: Object.values(metrics.social_media?.platforms || {}).map((p: any) => p.followers),
      backgroundColor: 'rgba(59, 130, 246, 0.6)',
      borderColor: 'rgba(59, 130, 246, 1)',
      borderWidth: 1
    }, {
      label: 'Engagement Rate',
      data: Object.values(metrics.social_media?.platforms || {}).map((p: any) => p.engagement_rate * 10000),
      backgroundColor: 'rgba(16, 185, 129, 0.6)',
      borderColor: 'rgba(16, 185, 129, 1)',
      borderWidth: 1
    }]
  } : null;

  // Content performance data
  const contentData = metrics?.content_performance ? {
    labels: Object.keys(metrics.content_performance?.content_types || {}),
    datasets: [{
      label: 'Average Views',
      data: Object.values(metrics.content_performance?.content_types || {}).map((c: any) =>
        c.avg_views || 0
      ),
      backgroundColor: [
        'rgba(147, 51, 234, 0.6)',
        'rgba(236, 72, 153, 0.6)',
        'rgba(251, 146, 60, 0.6)',
        'rgba(34, 197, 94, 0.6)'
      ],
      borderWidth: 1
    }]
  } : null;

  // Search Trends radar chart
  const searchTrendsData = metrics?.search_trends ? {
    labels: ['Search Interest', 'Brand Awareness', 'Competitor Gap', 'Regional Reach', 'Trend Momentum'],
    datasets: [{
      label: brandName,
      data: [
        metrics.search_trends?.search_interest_score || 0,
        metrics.search_trends?.brand_awareness_index || 0,
        metrics.search_trends?.competitor_gap || 0,
        metrics.search_trends?.regional_coverage || 0,
        metrics.search_trends?.trend_momentum || 0
      ],
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      pointBackgroundColor: 'rgba(59, 130, 246, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
    }]
  } : null;

  // Search Trends Over Time Line Chart (all brands)
  const searchTrendsOverTimeData = metrics?.search_trends?.trends_over_time ? {
    labels: metrics.search_trends.trends_over_time.labels,
    datasets: metrics.search_trends.trends_over_time.brands.map((brand: any) => ({
      label: brand.name,
      data: brand.data,
      borderColor: brand.color,
      backgroundColor: brand.color + '20',
      tension: 0.4,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2
    }))
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      },
      tooltip: {
        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDark ? '#fff' : '#111827',
        bodyColor: isDark ? '#E5E7EB' : '#374151',
        borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed || context.raw || 0;
            return `${label}: ${value}%`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      },
      y: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.08)'
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563'
        }
      }
    }
  };

  const radarOptions = {
    ...chartOptions,
    scales: {
      r: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        pointLabels: {
          color: isDark ? '#E5E7EB' : '#1F2937',
          font: {
            size: 11
          }
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          backdropColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)'
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };

  // Sentiment Score Gauge Component
  const SentimentGauge = ({ score }: { score: number }) => {
    const getColor = () => {
      if (score > 80) return 'from-primary to-cyan-500';
      if (score > 60) return 'from-amber-500 to-amber-600';
      return 'from-red-500 to-red-600';
    };

    return (
      <div className="flex flex-col items-center">
        <div className="relative w-32 h-32">
          <svg className="transform -rotate-90 w-32 h-32">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="url(#sentimentGradient)"
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${(score / 100) * 352} 352`}
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="sentimentGradient">
                <stop offset="0%" stopColor="#2563EB" />
                <stop offset="100%" stopColor="#06B6D4" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-foreground">{score.toFixed(0)}</span>
            <span className="text-xs text-text-muted">Sentiment</span>
          </div>
        </div>
        <span className={`mt-2 text-sm font-medium bg-gradient-to-r ${getColor()} text-transparent bg-clip-text`}>
          {score > 80 ? 'Excellent' : score > 60 ? 'Good' : 'Needs Attention'}
        </span>
      </div>
    );
  };

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-muted">No digital metrics available</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card rounded-2xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center">
              <i className="fas fa-globe mr-3 text-primary"></i>
              Digital Metrics
            </h2>
            <p className="text-text-muted mt-1">Comprehensive digital presence and engagement</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-text-muted">
              {isEnhancedMetrics ? 'Total Followers' : 'Monthly Mentions'}
            </p>
            <p className="text-2xl font-bold text-primary">
              {isEnhancedMetrics
                ? (metrics.social_media?.total_followers || 0).toLocaleString()
                : (metrics.mentions_count || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* View Selector for Enhanced Metrics */}
        {isEnhancedMetrics && (
          <div className="flex space-x-1 bg-background-secondary rounded-xl p-1 border border-border mt-4">
            <button
              onClick={() => setActiveView('social')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'social'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-share-alt mr-2"></i>
              Social Media
            </button>
            <button
              onClick={() => setActiveView('content')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'content'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-pen mr-2"></i>
              Content
            </button>
            <button
              onClick={() => setActiveView('search')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'search'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-chart-line mr-2"></i>
              Search Trends
            </button>
            <button
              onClick={() => setActiveView('paid')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                activeView === 'paid'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
              }`}
            >
              <i className="fas fa-ad mr-2"></i>
              Paid Media
            </button>
          </div>
        )}
      </div>

      {/* Social Media View */}
      {(!isEnhancedMetrics || activeView === 'social') && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Engagement</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {isEnhancedMetrics
                  ? `${((metrics.social_media?.avg_engagement_rate || 0) * 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
                  : `${((metrics.engagement_rate || 0) * 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`}
              </p>
              <p className="text-xs text-emerald-500 mt-1">
                <i className="fas fa-arrow-up mr-1"></i>+2.3% vs last month
              </p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Share of Voice</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {isEnhancedMetrics
                  ? `${(metrics.competitor_analysis?.share_of_voice || 0).toFixed(1)}%`
                  : `${(metrics.share_of_voice || 0).toFixed(1)}%`}
              </p>
              <div className="w-full bg-background-tertiary rounded-full h-1 mt-2">
                <div className="bg-gradient-to-r from-primary to-primary/80 h-1 rounded-full"
                  style={{ width: `${metrics.competitor_analysis?.share_of_voice || metrics.share_of_voice || 0}%` }}>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Viral Potential</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {isEnhancedMetrics
                  ? `${(metrics.trend_analysis?.viral_potential || 0).toFixed(0)}`
                  : `${(metrics.viral_coefficient || 0).toFixed(2)}`}
              </p>
              <p className="text-xs text-text-muted mt-1">Content shareability</p>
            </div>
            <div className="bg-card rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Community Health</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {isEnhancedMetrics
                  ? `${(metrics.social_media?.community_health || 0).toFixed(0)}%`
                  : `${(metrics.sentiment_score || 0).toFixed(0)}%`}
              </p>
              <p className="text-xs text-text-muted mt-1">Overall sentiment</p>
            </div>
          </div>

          {/* Platform Performance (Enhanced Metrics Only) */}
          {isEnhancedMetrics && platformData && (
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-foreground">Platform Performance</h3>
                <div className="relative">
                  <select
                    value={contentTypeFilter}
                    onChange={(e) => setContentTypeFilter(e.target.value as 'all' | 'brand' | 'community')}
                    className="appearance-none bg-card border border-[var(--border)] rounded-full px-3 py-1.5 pr-7 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/30 transition-all cursor-pointer"
                  >
                    <option value="all">All Content</option>
                    <option value="brand">Brand</option>
                    <option value="community">Community</option>
                  </select>
                  <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-[10px] pointer-events-none"></i>
                </div>
              </div>
              <div className="h-80">
                <Bar data={platformData} options={chartOptions} />
              </div>

              {/* Platform Details Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                {Object.entries(metrics.social_media?.platforms || {}).map(([platform, data]: [string, any]) => (
                  <div key={platform} className="bg-background-secondary rounded-lg p-3 border border-border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-foreground font-medium">{platform}</span>
                      <i className={`fab fa-${platform.toLowerCase()} text-primary`}></i>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Followers</span>
                        <span className="text-foreground">{(data.followers / 1000).toFixed(1)}K</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Engagement</span>
                        <span className="text-foreground">{data.engagement_rate.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">Growth</span>
                        <span className="text-emerald-500">+{data.growth_rate.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sentiment Analysis Chart */}
          {isEnhancedMetrics && (
            <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
              <style>{`
                @keyframes flashColors {
                  0%, 100% { background: linear-gradient(90deg, #10b981, #22c55e); }
                  50% { background: linear-gradient(90deg, #facc15, #eab308); }
                }
              `}</style>
              <div
                className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
                style={{
                  animation: 'flashColors 1s ease-in-out infinite',
                  background: 'linear-gradient(90deg, #10b981, #facc15)'
                }}
              >
                ✨ NEW
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Sentiment Analysis</h3>
              <div className="flex justify-center">
                <div className="h-72 w-full max-w-sm">
                  <Doughnut
                    data={sentimentData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom' as const,
                          labels: {
                            color: isDark ? '#E5E7EB' : '#374151',
                            padding: 20,
                            usePointStyle: true
                          }
                        },
                        tooltip: {
                          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                          titleColor: isDark ? '#fff' : '#111827',
                          bodyColor: isDark ? '#E5E7EB' : '#374151',
                          borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                          borderWidth: 1
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Content Performance View */}
      {isEnhancedMetrics && activeView === 'content' && contentData && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-xl font-semibold text-foreground mb-4">Content Performance</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80">
              <Bar data={contentData} options={chartOptions} />
            </div>
            <div className="space-y-4">
              {Object.entries(metrics.content_performance?.content_types || {}).map(([type, data]: [string, any]) => (
                <div key={type} className="bg-background-secondary rounded-lg p-4 border border-border">
                  <h4 className="text-foreground font-medium capitalize mb-2">{type}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(data).slice(0, 4).map(([key, value]: [string, any]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-text-muted">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                        <span className="text-foreground">
                          {typeof value === 'number'
                            ? value > 100 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(1)
                            : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <p className="text-text-muted text-sm">Content ROI</p>
              <p className="text-2xl font-bold text-foreground">{metrics.content_performance?.content_roi || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-text-muted text-sm">Content Velocity per Month</p>
              <p className="text-2xl font-bold text-foreground">{metrics.content_performance?.content_velocity || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-text-muted text-sm">Effectiveness</p>
              <p className="text-2xl font-bold text-foreground">{metrics.content_performance?.content_effectiveness || 0}%</p>
            </div>
            <div className="text-center">
              <p className="text-text-muted text-sm">Evergreen Ratio</p>
              <p className="text-2xl font-bold text-foreground">{((metrics.content_performance?.evergreen_ratio || 0) * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Trends View */}
      {isEnhancedMetrics && activeView === 'search' && searchTrendsData && (
        <div className="space-y-6">
          <style>{`
            @keyframes flashColors {
              0%, 100% { background: linear-gradient(90deg, #10b981, #22c55e); }
              50% { background: linear-gradient(90deg, #facc15, #eab308); }
            }
          `}</style>
          {/* Search Momentum Gauge */}
          <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
            <div
              className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
              style={{
                animation: 'flashColors 1s ease-in-out infinite',
                background: 'linear-gradient(90deg, #10b981, #facc15)'
              }}
            >
              ✨ NEW
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Search Momentum</h3>
            <p className="text-text-muted text-sm mb-6">Current month search performance score</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Gauge */}
              <div className="flex flex-col items-center justify-center">
                <div className="relative w-64 h-64">
                  {/* Outer ring - background */}
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
                    {/* Background circle */}
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                      strokeWidth="16"
                    />
                    {/* Last month comparison (inner) */}
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="none"
                      stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                      strokeWidth="10"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="65"
                      fill="none"
                      stroke="rgba(156, 163, 175, 0.5)"
                      strokeWidth="10"
                      strokeDasharray={`${((metrics.search_trends?.last_month_score || 70) / 100) * 408} 408`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                    {/* Current month progress (outer) */}
                    <circle
                      cx="100"
                      cy="100"
                      r="80"
                      fill="none"
                      stroke="url(#gaugeGradient)"
                      strokeWidth="16"
                      strokeDasharray={`${((metrics.search_trends?.search_interest_score || 0) / 100) * 502} 502`}
                      strokeLinecap="round"
                      className="transition-all duration-1000"
                    />
                    {/* Gradient definition */}
                    <defs>
                      <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="50%" stopColor="#8B5CF6" />
                        <stop offset="100%" stopColor="#EC4899" />
                      </linearGradient>
                    </defs>
                  </svg>
                  {/* Center content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-5xl font-bold text-foreground">{metrics.search_trends?.search_interest_score || 0}</span>
                    <span className="text-text-muted text-sm mt-1">out of 100</span>
                    <div className={`flex items-center gap-1 mt-2 text-sm ${metrics.search_trends?.trend_direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                      <i className={`fas fa-arrow-${metrics.search_trends?.trend_direction || 'up'}`}></i>
                      <span>{metrics.search_trends?.trend_change || 0}%</span>
                      <span className="text-text-muted">vs last month</span>
                    </div>
                  </div>
                </div>
                {/* Legend */}
                <div className="flex gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                    <span className="text-sm text-text-muted">This Month</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-gray-400/50" />
                    <span className="text-sm text-text-muted">Last Month</span>
                  </div>
                </div>
              </div>

              {/* Search Categories Breakdown */}
              <div className="space-y-4">
                <h4 className="text-foreground font-medium">Top Search Categories</h4>
                <div className="space-y-3">
                  {[
                    { name: 'Brand Name Searches', score: 92, color: '#3B82F6' },
                    { name: 'Product Searches', score: 78, color: '#8B5CF6' },
                    { name: 'Comparison Searches', score: 65, color: '#EC4899' },
                    { name: 'Review Searches', score: 58, color: '#F59E0B' },
                    { name: 'Where to Buy', score: 45, color: '#10B981' }
                  ].map((category, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-muted">{category.name}</span>
                        <span className="text-foreground font-medium">{category.score}%</span>
                      </div>
                      <div className="w-full bg-background-tertiary rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-700"
                          style={{ width: `${category.score}%`, backgroundColor: category.color }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>

          {/* Search Interest KPI Cards */}
          <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
            <div
              className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
              style={{
                animation: 'flashColors 1s ease-in-out infinite',
                background: 'linear-gradient(90deg, #10b981, #facc15)'
              }}
            >
              ✨ NEW
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-4">Search Interest Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-background-secondary rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Search Interest Score</p>
              <p className="text-2xl font-bold text-foreground">{metrics.search_trends?.search_interest_score || 0}</p>
              <p className={`text-xs mt-1 ${metrics.search_trends?.trend_direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                <i className={`fas fa-arrow-${metrics.search_trends?.trend_direction || 'up'} mr-1`}></i>
                {metrics.search_trends?.trend_change || 0}% vs last period
              </p>
            </div>
            <div className="bg-background-secondary rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Peak Search Month</p>
              <p className="text-2xl font-bold text-foreground">{metrics.search_trends?.peak_month || 'N/A'}</p>
              <p className="text-xs text-text-muted mt-1">Highest search volume</p>
            </div>
            <div className="bg-background-secondary rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">vs Top Competitor</p>
              <p className="text-2xl font-bold text-foreground">{metrics.search_trends?.competitor_gap > 0 ? '+' : ''}{metrics.search_trends?.competitor_gap || 0}%</p>
              <p className="text-xs text-text-muted mt-1">Search share difference</p>
            </div>
            <div className="bg-background-secondary rounded-xl p-4 border border-border">
              <p className="text-text-muted text-sm">Seasonal Index</p>
              <p className="text-2xl font-bold text-foreground">{metrics.search_trends?.seasonal_index || 0}</p>
              <p className="text-xs text-text-muted mt-1">{metrics.search_trends?.current_season || 'Current'} trend</p>
            </div>
            </div>
          </div>

          {/* Search Trends Over Time Chart */}
          {searchTrendsOverTimeData && (
            <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
              <div
                className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
                style={{
                  animation: 'flashColors 1s ease-in-out infinite',
                  background: 'linear-gradient(90deg, #10b981, #facc15)'
                }}
              >
                ✨ NEW
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Search Interest Over Time</h3>
              <p className="text-text-muted text-sm mb-4">Comparing search trends across all brands in the category</p>
              <div className="h-80">
                <Line data={searchTrendsOverTimeData} options={chartOptions} />
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-border">
                {metrics.search_trends?.trends_over_time?.brands?.map((brand: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: brand.color }} />
                    <span className="text-sm text-text-muted">{brand.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* Paid Media View - Locked */}
      {isEnhancedMetrics && activeView === 'paid' && (
        <div className="space-y-6">
          <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
            {/* Blurred Content Preview */}
            <div className="filter blur-sm opacity-50 pointer-events-none">
              <h3 className="text-xl font-semibold text-foreground mb-4">Paid Media Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['Google Ads', 'Meta Ads', 'TikTok Ads'].map((channel) => (
                  <div key={channel} className="bg-background-secondary rounded-lg p-4 border border-border">
                    <h4 className="text-foreground font-medium mb-3">{channel}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-text-muted text-sm">CTR</span>
                        <span className="text-foreground font-medium">2.4%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted text-sm">CPC</span>
                        <span className="text-foreground font-medium">$0.45</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-muted text-sm">ROAS</span>
                        <span className="text-foreground font-medium">3.2x</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="text-center">
                  <p className="text-text-muted text-sm">Overall ROAS</p>
                  <p className="text-2xl font-bold text-foreground">3.5x</p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-sm">Cost per Acquisition</p>
                  <p className="text-2xl font-bold text-foreground">$12.50</p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-sm">Budget Utilization</p>
                  <p className="text-2xl font-bold text-foreground">87%</p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-sm">Attribution Model</p>
                  <p className="text-lg font-medium text-foreground">Last Click</p>
                </div>
              </div>
            </div>

            {/* Lock Overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px]">
              <div className="text-center max-w-md mx-auto">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
                  <i className="fas fa-lock text-primary text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">Premium Feature</h3>
                <p className="text-text-muted text-sm mb-5">
                  Paid Media Analytics is not included in your current package. Unlock detailed insights on your advertising performance across all platforms.
                </p>
                <a
                  href="mailto:info@silainsights.com?subject=Upgrade Package - Paid Media Analytics"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-medium text-sm hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25"
                >
                  <i className="fas fa-envelope"></i>
                  Contact to Upgrade
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts for Basic Metrics */}
      {!isEnhancedMetrics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sentiment Analysis */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <h3 className="text-xl font-semibold text-foreground mb-4">Sentiment Analysis</h3>
            <div className="h-64">
              <Doughnut data={sentimentData} options={{ ...chartOptions, maintainAspectRatio: false }} />
            </div>
          </div>

          {/* Sentiment Score */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <div className="flex flex-col items-center justify-center h-full">
              <h3 className="text-xl font-semibold text-foreground mb-4">Overall Sentiment</h3>
              <SentimentGauge score={metrics.sentiment_score || 70} />
            </div>
          </div>

          {/* Trending Topics */}
          <div className="bg-card rounded-2xl p-6 border border-border">
            <h3 className="text-xl font-semibold text-foreground mb-4">Trending Topics</h3>
            <div className="flex flex-wrap gap-3">
              {(metrics.trending_topics || []).map((topic: string, index: number) => (
                <div
                  key={index}
                  className="px-4 py-2 bg-primary/10 rounded-full border border-primary/30 hover:border-primary/50 transition-all hover:scale-105"
                >
                  <span className="text-primary font-medium">#{topic}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Digital Insights - Only locked in Paid Media tab */}
      {isEnhancedMetrics && activeView !== 'paid' && (
        <div className="bg-card rounded-2xl p-6 border border-border">
          <h3 className="text-xl font-semibold text-foreground mb-4">
            <i className="fas fa-lightbulb text-amber-500 mr-2"></i>
            Digital Insights
          </h3>
          <div className="space-y-3">
            {activeView === 'social' && [
              'Social engagement is trending upward with a 12% increase in positive sentiment this month.',
              'Instagram shows the highest engagement rate at 4.2%, outperforming industry average by 1.8x.',
              'Peak engagement hours identified: 9-11 AM and 7-9 PM local time.',
              'User-generated content drives 3x more engagement than branded posts.',
              'Recommended: Increase video content frequency to capitalize on current algorithm preferences.'
            ].map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-background-secondary/50 rounded-lg">
                <i className="fas fa-chart-line text-primary mt-0.5"></i>
                <p className="text-text-secondary text-sm">{insight}</p>
              </div>
            ))}
            {activeView === 'content' && [
              'Video content outperforms static images by 2.5x in engagement metrics.',
              'Carousel posts generate 1.8x more saves and shares than single images.',
              'Content with brand storytelling shows 40% higher completion rates.',
              'Optimal post length: 150-200 characters for maximum engagement.',
              'Recommended: Focus on educational and behind-the-scenes content for Q1.'
            ].map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-background-secondary/50 rounded-lg">
                <i className="fas fa-file-alt text-purple-500 mt-0.5"></i>
                <p className="text-text-secondary text-sm">{insight}</p>
              </div>
            ))}
            {activeView === 'search' && [
              'Brand search volume increased 18% month-over-month indicating growing awareness.',
              'Top converting keywords: brand name + "reviews", "best", "near me".',
              'Competitor gap analysis shows opportunity in long-tail keyword targeting.',
              'Regional search interest strongest in Dubai and Abu Dhabi markets.',
              'Recommended: Optimize for voice search queries trending in your category.'
            ].map((insight, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-background-secondary/50 rounded-lg">
                <i className="fas fa-search text-cyan-500 mt-0.5"></i>
                <p className="text-text-secondary text-sm">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Digital Insights - Locked in Paid Media tab */}
      {isEnhancedMetrics && activeView === 'paid' && (
        <div className="bg-card rounded-2xl p-6 border border-border relative overflow-hidden">
          {/* Blurred Content Preview */}
          <div className="filter blur-sm opacity-50 pointer-events-none">
            <h3 className="text-xl font-semibold text-foreground mb-4">Digital Insights</h3>
            <div className="space-y-2">
              {['AI-powered analysis of your brand performance across digital channels',
                'Sentiment trends and key drivers identified from social listening',
                'Competitive benchmarking insights and market positioning',
                'Recommended actions based on data patterns',
                'Weekly performance summary and anomaly detection'].map((insight, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <i className="fas fa-lightbulb text-amber-500 mt-1"></i>
                  <p className="text-text-secondary">{insight}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lock Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[2px]">
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
                <i className="fas fa-lock text-primary text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Premium Feature</h3>
              <p className="text-text-muted text-sm mb-5">
                Digital Insights powered by AI is not included in your current package. Unlock actionable recommendations and deep analysis.
              </p>
              <a
                href="mailto:info@silainsights.com?subject=Upgrade Package - Digital Insights"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full font-medium text-sm hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25"
              >
                <i className="fas fa-envelope"></i>
                Contact to Upgrade
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalMetricsDashboard;